import React, { useState, useEffect, useMemo, useRef } from 'react';

import eventCaption from '../../../../assets/event-caption.png';
import eventDonation from '../../../../assets/event-donation.png';

import { FloatingLabel, FormText } from '../../../../components/form';
import { Switch } from '../../../../components/general';

import { useProjectSettingsStore } from '../../../../stores/projectSettings';

import { Event } from '../../../../types';
import { getHashTagText } from '../../../../utils';

import ShareVideoUI from '../../../Share/components/shareVideoUI';
import InfoImg from '../components/infoImg';
import TabComponent from '../components/tabComponent';

interface Props {
  event: Event;
}

const ShareVideoTab: React.FC<Props> = (props) => {
  const { event } = props;

  const { previewPerson } = useProjectSettingsStore();

  const [data, setData] = useState<Partial<Event>>({
    caption: event.caption ?? '',
    hashTag: event.hashTag ?? [],
  });

  const hashTagText = useMemo(
    () => getHashTagText(data.hashTag ?? []),
    [data.hashTag],
  );

  const paymentObj = useMemo(() => event.payment ?? null, [event.payment]);

  const [allowDonations, setAllowDonations] = useState(
    paymentObj?.allowDonations ?? false,
  );

  const previewRef = useRef<HTMLDivElement>(null);
  const [previewScreen, setPreviewScreen] = useState(0);
  const [previewWidth, setPreviewWidth] = useState(0);

  useEffect(() => {
    if (previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      setPreviewWidth(rect.width);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let valueUpd: any = value;

    if (name === 'hashTag')
      valueUpd = value
        .split(/[\s#,.]+/)
        .filter((tag, index) => tag !== '' || index !== 0);

    setData((prevData) => ({ ...prevData, [name]: valueUpd }));
  };

  return (
    <TabComponent
      title="Share Page"
      subtitle={
        <>
          Customise the project Share Video page, where your attendees can watch
          and share their personalised videos.
        </>
      }
      event={event}
      data={{ ...data, payment: { allowDonations } }}
      blocks={[
        {
          title: 'Caption and Hashtags',
          description:
            'Provide your attendees with a caption and hashtags they can use in their social media posts',
          infoHeader: 'Setting up your Caption & Hashtags',
          infoBody: (
            <React.Fragment>
              <div>
                Together with video, your attendees will receive hashtags and
                caption to copy and paste to social media.
              </div>

              <InfoImg src={eventCaption} alt="Caption Graphic" />
            </React.Fragment>
          ),
          content: (
            <React.Fragment>
              <FloatingLabel
                label="Caption"
                as="textarea"
                name="caption"
                value={data.caption ?? ''}
                onChange={handleChange}
              />

              <div>
                <FloatingLabel
                  label="Hashtag"
                  name="hashTag"
                  value={hashTagText}
                  onChange={handleChange}
                />

                <FormText text="#crowdclip hashtag will be added to the list automatically" />
              </div>
            </React.Fragment>
          ),
        },
        {
          isHide: !paymentObj?.paid,
          title: 'Donations',
          description:
            'Allow attendees to make a donation or disable this option',
          infoHeader: 'Setting up your Donations',
          infoBody: (
            <React.Fragment>
              <div>
                The attendees will be able to contribute any amount by clicking
                the 'Donate' button.
              </div>

              <InfoImg src={eventDonation} alt="Donation Graphic" />
            </React.Fragment>
          ),
          content: (
            <Switch
              id="settings-donations-switch"
              label="Allow Donations"
              checked={allowDonations}
              onChange={() => setAllowDonations(!allowDonations)}
            />
          ),
        },
      ]}
      preview={{
        previewRef: previewRef,
        show: previewScreen !== -1,
        onShow: () => setPreviewScreen(0),
        onHide: () => setPreviewScreen(-1),
        content: (
          <ShareVideoUI
            isPreview
            width={previewWidth}
            event={{
              ...event,
              ...data,
              payment: { paid: paymentObj?.paid ?? false, allowDonations },
            }}
            person={previewPerson}
          />
        ),
        note: 'Some of the actions are disabled in preview',
      }}
    />
  );
};

export default ShareVideoTab;
